// SEO Types and Interfaces

export interface SEOScore {
  overall: number;
  metaTags: number;
  content: number;
  technical: number;
  keywords: number;
}

export interface SEOIssue {
  id: string;
  type: 'error' | 'warning' | 'info';
  category: 'meta' | 'content' | 'images' | 'technical' | 'keywords' | 'links';
  title: string;
  description: string;
  page: string;
  pageUrl: string;
  howToFix: string;
  impact: 'high' | 'medium' | 'low';
}

export interface PageSEOAnalysis {
  url: string;
  title: string;
  score: number;
  lastAnalyzed: string;
  meta: {
    title: { value: string; length: number; status: 'pass' | 'warning' | 'fail'; message: string };
    description: { value: string; length: number; status: 'pass' | 'warning' | 'fail'; message: string };
    canonical: { value: string; status: 'pass' | 'warning' | 'fail'; message: string };
  };
  headings: {
    h1: { count: number; values: string[]; status: 'pass' | 'warning' | 'fail'; message: string };
    h2: { count: number; values: string[] };
    h3: { count: number; values: string[] };
  };
  content: {
    wordCount: number;
    status: 'pass' | 'warning' | 'fail';
    message: string;
    readabilityScore: number;
    readabilityGrade: string;
  };
  images: {
    total: number;
    withAlt: number;
    withoutAlt: string[];
    status: 'pass' | 'warning' | 'fail';
    message: string;
  };
  links: {
    internal: number;
    external: number;
    broken: string[];
    status: 'pass' | 'warning' | 'fail';
    message: string;
  };
  keywords: KeywordAnalysis[];
}


export interface KeywordAnalysis {
  keyword: string;
  targetPage: string;
  inTitle: boolean;
  inH1: boolean;
  inUrl: boolean;
  inFirst100Words: boolean;
  density: number;
  occurrences: number;
  status: 'good' | 'needs-work' | 'missing';
}

export interface TrackedKeyword {
  id: string;
  keyword: string;
  targetPageId: string;
  targetPageUrl: string;
  targetPageTitle: string;
  isPrimary: boolean;
  createdAt: string;
  lastChecked: string;
  analysis?: KeywordAnalysis;
}

export interface SuggestedKeyword {
  keyword: string;
  category: 'primary' | 'long-tail' | 'local' | 'service' | 'comparison';
  searchVolume?: 'high' | 'medium' | 'low';
  difficulty?: 'easy' | 'medium' | 'hard';
}

export interface TechnicalSEO {
  sitemap: {
    exists: boolean;
    lastGenerated?: string;
    pageCount: number;
    url?: string;
  };
  robotsTxt: {
    exists: boolean;
    content?: string;
  };
  https: {
    enabled: boolean;
    message: string;
  };
  mobileFriendly: {
    status: boolean;
    message: string;
  };
  brokenLinks: {
    count: number;
    links: { url: string; foundOn: string }[];
  };
  pageSpeed: {
    score: number;
    issues: string[];
  };
}

export interface SEOReport {
  generatedAt: string;
  overallScore: number;
  totalPages: number;
  totalIssues: number;
  issuesByCategory: Record<string, number>;
  topIssues: SEOIssue[];
  scoreHistory: { date: string; score: number }[];
}

// Tooltip content for SEO education
export const SEO_TOOLTIPS = {
  seoScore: "A number from 0-100 showing how well your website is optimized for Google. Higher is better. Aim for 80+.",
  metaTitle: "The clickable headline that appears in Google search results. Keep it 50-60 characters and include your main keyword.",
  metaDescription: "The short summary below your title in Google. Write 150-160 characters that make people want to click.",
  h1Tag: "The main headline on your page. Google uses this to understand what your page is about. Use only ONE per page.",
  h2Tag: "Subheadings that break up your content. Use them to organize your page and include related keywords.",
  keywordDensity: "How often your target keyword appears compared to total words. Aim for 1-2%. Too high looks spammy, too low means weak targeting.",
  altText: "A description of an image for people who can't see it. Google also uses this to understand images. Be descriptive!",
  internalLinks: "Links from one page on your site to another. Helps visitors navigate and helps Google understand your site structure.",
  externalLinks: "Links to other websites. Use sparingly and link to authoritative sources. Add rel='nofollow' for untrusted links.",
  sitemap: "A file that lists all pages on your website. Submit it to Google so they can find all your content.",
  robotsTxt: "A file that tells search engines which pages they can and cannot crawl. Be careful not to block important pages!",
  readabilityScore: "How easy your content is to read. Aim for 60-70 (8th grade level). Simpler text = more readers.",
  wordCount: "The number of words on a page. Main pages should have 800+ words, blog posts 1000-2000 words for better ranking.",
  canonical: "Tells Google which version of a page is the 'main' one. Prevents duplicate content issues.",
  https: "Secure sites (https://) rank better than non-secure (http://). Google marks non-https sites as 'Not Secure'.",
  mobileFriendly: "Google uses mobile-first indexing, meaning it primarily looks at your mobile site. Make sure it works well on phones!",
  brokenLinks: "Links that lead to error pages (404). They hurt user experience and SEO. Fix or remove them.",
  pageSpeed: "How fast your pages load. Faster pages rank better and convert more visitors into customers.",
  primaryKeyword: "The main word or phrase you want a page to rank for. Each page should target ONE primary keyword.",
  longTailKeyword: "Longer, more specific phrases like 'cheap car rental Perth airport'. Easier to rank for and often convert better.",
};
